<template>
    <div v-if="slice.order" class="order">
        <p class="order-title" v-html="slice.order.title" />

        <template v-if="!hasList">
            <div class="order-start" v-html="textStart" />
        </template>

        <template v-else>
            <div
                v-for="(product, index) in list"
                :key="`order-product-${index}`"
                class="order-product"
            >
                <div v-if="product.title" class="product-item">
                    <p class="product-title" v-html="product.titleLicense || product.title" />
                    <div class="product-price">
                        <template v-if="product.discount">
                            <div class="price-full" v-html="currentPrice(product, 'discount')" />
                        </template>
                        <div class="price-current" v-html="currentPrice(product, 'price')" />
                    </div>
                    <div class="remove" @click="$emit('remove', { id: product.cleverBridgeCartID, isAddon: false })">
                        <a-glyph name="i-delete-o--16" />
                    </div>
                </div>

                <p class="period" v-html="periodText(product)" />
                <template v-if="!product.quantity.isHidden">
                    <p class="product-quantity" v-html="quantityText(product)" />
                </template>

                <div v-if="product.addon?.selected?.quantity" class="product-item product-item-addon">
                    <div>{{ quantityText(product.addon) }} </div>
                    <div class="price-current" v-html="priceAddon(product.addon)" />
                    <div class="remove" @click="$emit('remove', { id: product.cleverBridgeCartID, isAddon: true })">
                        <a-glyph name="i-delete-o--16" />
                    </div>
                </div>
            </div>

            <div v-if="slice.order.total" class="total">
                <div class="total-item">
                    <p class="total-text" v-html="slice.order.total" />
                    <p
                        v-if="slice.order.vat" 
                        class="total-vat"
                        v-html="slice.order.vat"
                    />
                    <p
                        v-else-if="slice.order.saveText && hasDiscount()"
                        class="total-save-text" 
                        v-html="slice.order.saveText"
                    />
                </div>
                <div class="total-item">
                    <template v-if="hasDiscount()">
                        <div class="total-full" v-html="setCurrency(price + discount)" />
                        <div class="total-discount" v-html="setCurrency(discount)" />
                    </template>
                    <div class="total-current" v-html="setCurrency(price)" />
                </div>
            </div>

            <a-button
                v-if="slice.order?.button?.title"
                v-bind="slice.order.button"
                :text="slice.order.button.title"
                :type="slice.order.button?.type || 'action'"
                :glyph="slice.order.button?.glyph || 'shopping-cart-o'"
                @click="handleClick"
            />
        </template>

        <div class="links">
            <p v-for="(item, i) in links" :key="`link-${i}`" class="links-item">
                <a-link v-if="item.to" v-bind="item" :text="item.title" />
                <span v-else v-html="item.title" />
            </p>
        </div>

        <p v-for="(item, i) in notifications" :key="`notification-${i}`" class="notifications">
            <a-glyph v-if="item.glyph" :name="item.glyph" />
            <span v-html="item.title || ''" />
        </p>

        <a-loader :is-visible="isVisibleSURL" />
    </div>
</template>

<script>
import AButton from '@core/components/button/button.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALink from '@core/components/link/link.vue';
import ALoader from '@core/components/loader/loader.vue';
import analytics from '@core/mixins/analytics.js';
import cleverbridge from '@core/mixins/cleverbridge.js';
import currency from '@core/mixins/currency.js';

export default {
    name: 'SPurchasingOrder',

    components: {
        AButton,
        AGlyph,
        ALink,
        ALoader,
    },

    mixins: [currency, cleverbridge, analytics],

    props: {
        slice: {
            type: Object,
            default: null,
        },
        promo: {
            type: Object,
            default: null,
        },
        settings: {
            type: Object,
            default: null,
        },
        productList: {
            type: Array,
            default: null,
        },
    },

    emits: ['remove'],

    data() {
        return {
            price: 0,
            discount: 0,
            isVisibleSURL: false,
        };
    },

    computed: {
        list() {
            const products = this.productList || [];
            return products.filter((item) => item?.selected?.quantity);
        },
        products() {
            const products = this.productList || [];
            const addons = products.map((item) => item.addon);
            return [...products, ...addons].filter((item) => item?.selected?.quantity);
        },
        hasList() {
            return Boolean(this.list.length);
        },
        order() {
            return this.slice?.order || {};
        },
        links() {
            return this.order.links || [];
        },
        notifications() {
            return this.order.notifications || [];
        },
        textStart() {
            return this.order.start || '';
        },
    },

    methods: {
        dictionary(item) {
            return this.dictionary?.[item] || this.settings?.dictionary?.[item] || item;
        },
        periodText(product) {
            const text = this.dictionary(product.selector?.title || 'Subscription period');
            const period = this.dictionary(`${product.selected?.period}Y-period`);
            return `${text}: ${period}`;
        },
        quantityText(product) {
            const text = product?.quantity?.title || this.dictionary('Quantity');
            const quantity = product?.selected?.quantity || '';
            const suffix = product?.quantity?.suffix || '';
            return `${text}: ${quantity}${suffix}`;
        },
        setPrice(type) {
            return this.products
                .map((product) => {
                    if (product[`${type}Mix`]) return product[`${type}Mix`];
                    const price = product[type] || 0;
                    const step = product.quantity?.step || 1;
                    const quantity = product.selected.quantity / step;
                    return price * quantity;
                })
                .reduce((sum, price) => sum + price, 0);
        },
        currentPrice(product, type) {
            const step = product.quantity?.step || 1;
            const quantity = product.selected.quantity / step || 1;
            const price = product.priceMix || product.price * quantity;
            const discount = product.discountMix || product.discount * quantity;
            return this.setCurrency(type === 'price' ? price : price + discount);
        },

        priceAddon(product) {
            return this.setCurrency(product.priceMix || product.price);
        },

        hasDiscount() {
            this.price = this.setPrice('price');
            this.discount = this.setPrice('discount');
            return this.discount;
        },
        handleClick() {
            const ecommerceList = {};
            const options = this.storeOptionsSURL({
                shopId: 837,
                segment: 'corporate',
                params: { scope: 'checkout' },
            });

            this.products
                .filter((product) => !product.priceMix)
                .forEach((product) => {
                    const step = product.quantity?.step || 1;
                    const quantity = product.selected.quantity / step;
                    options.params.cart += `,${product.cleverBridgeCartID}`;
                    options.params[`quantity_${product.cleverBridgeCartID}`] = quantity;
                    ecommerceList[product.cleverBridgeCartID] = quantity;

                    const minQuantity = Math.min(...product.licenses.map((item) => item.maxQuantity));
                    if (minQuantity && quantity >= minQuantity) {
                        options.params.lockcart = true;
                    }
                });

            this.products
                .filter((product) => product.priceMix)
                .forEach((product) => {
                    product.selected.list.forEach((item) => {
                        options.params.cart += `,${item.cleverBridgeCartID}`;
                        options.params[`quantity_${item.cleverBridgeCartID}`] = item.quantity;
                        ecommerceList[item.cleverBridgeCartID] = item.quantity;
                    });
                });

            options.params.cart = options.params.cart.substring(1);

            if (this.promo?.isPromoActive && this.promo?.coupon) {
                const hasPromoID = this.promo.cleverBridgeCartList.some(
                    (item) => options.params.cart.includes(item),
                );
                if (hasPromoID) {
                    options.params.coupon = this.promo.coupon;
                }
            }

            if (this.activeCoupon.coupon) {
                options.params.coupon = this.activeCoupon.coupon;
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'AcronisEcommerce',
                eventLabel: options.params.cart,
                eventAction: 'Proceed to checkout',
                eventContext: 'add',
                eventCategory: 'Conversions',
                eventContent: this.brandName,
                eventLocation: this.pageType,
                eventProductName: '',
                eventProductPrice: this.price,
                eventProductId: options.params.cart,
                eventPosition: 1,
                ecommerce: {
                    currencyCode: this.productCurrency,
                    add: { products: this.ecommerceProducts(ecommerceList)[0] },
                },
            });

            this.redirectToCleverbridge(options);
        },
    },
};
</script>

<style lang="postcss" scoped>
.order {
    margin-top: 24px;
    color: var(--av-nav-primary);
    @media (--viewport-tablet) {
        width: 50%;
        margin-top: 16px;
    }
    @media (--viewport-desktop) {
        width: 100%;
        max-width: 309px;
    }
    @media (--viewport-desktop-wide) {
        max-width: 349px;
    }
    @media (--viewport-desktop-large) {
        max-width: 403px;
    }
}
.order-title {
    @mixin title-accent;
    margin-bottom: 24px;
}
.order-product {
    padding: 24px 0 16px;
    border-top: 1px solid var(--av-brand-secondary-light);
}
.product-item {
    gap: 4px;
    display: flex;
    align-items: center;
}
.product-item-addon {
    margin-top: 16px;
    .price-current {
        margin-inline-start: auto;
    }
}
.product-title {
    @mixin lead-accent;
    width: 100%;
    padding-inline-end: 24px;
}
.product-price {
    @mixin lead-strong;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}
.price-full {
    @mixin lead;
    font-weight: 400;
    position: relative;
    color: var(--av-fixed-light);
    &:before {
        inset-inline-start: 0;
        top: 58%;
        height: 1px;
        content: "";
        width: 100%;
        position: absolute;
        background: var(--av-fixed-light);
    }
}
.price-current {
    @mixin lead-accent;
    color: var(--av-nav-primary);
}
.remove {
    .a-glyph {
        width: 32px;
        height: 32px;
        padding: 8px;
        display: block;
        cursor: pointer;
        border-radius: 4px;
        fill: var(--av-brand-primary);
        &:hover {
            background-color: var(--av-brand-secondary-accent);
        }
        &:active {
            background-color: var(--av-brand-secondary-light);
        }
    }
}
.product-quantity {
    @mixin paragraph;
    margin-top: 16px;
    color: var(--av-fixed-secondary);
}
.period {
    color: var(--av-fixed-secondary);
    span {
        white-space: nowrap;
    }
}
.total {
    display: flex;
    padding: 16px 0 0;
    align-items: flex-start;
    justify-content: space-between;
    border-top: 1px solid var(--av-brand-secondary-light);
}
.total-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    &:not(:first-child) {
        align-items: flex-end;
    }
}
.total-text {
    @mixin title;
    color: var(--av-nav-primary);
}
.total-vat {
    @mixin paragraph;
    margin-top: 8px;
    color: var(--av-fixed-secondary);
}
.total-full {
    @mixin title;
    position: relative;
    color: var(--av-fixed-light);
    &:before {
        inset-inline-start: 0;
        top: 58%;
        height: 1px;
        content: "";
        width: 100%;
        position: absolute;
        background: var(--av-fixed-light);
    }
}
.total-discount {
    @mixin lead-accent;
    color: var(--av-fixed-success);
}
.total-save-text {
    color: var(--av-fixed-success);
} 
.total-current {
    @mixin display-accent;
}
.order-start {
    @mixin lead;
    text-align: center;
    padding: 80px 0 64px;
    color: var(--av-fixed-secondary);
    border-top: 1px solid var(--av-brand-secondary-light);
    border-bottom: 1px solid var(--av-brand-secondary-light);
    & ~ .links {
        padding-top: 56px;
    }
}
.links {
    padding: 24px 0;
}
.links-item {
    &:not(:first-child) {
        margin: 8px 0 0;
    }
    &:deep(a) {
        text-decoration: none;
        color: var(--av-brand-primary);

        &:hover {
            color: var(--av-brand-secondary);
        }
    }
    &:deep(.a-link__content) {
        @mixin paragraph-accent;

        .a-glyph {
            margin-inline-start: 0;
        }
    }
}

.notifications {
    @mixin paragraph;
    margin: 24px 0 0;
    position: relative;
    padding: 16px 24px;
    padding-inline-start: 48px;
    background: var(--av-solid-brand-secondary-accent);
    .a-glyph {
        top: 20px;
        inset-inline-start: 24px;
        position: absolute;
    }
    &:deep(b) {
        font-weight: 600;
    }
    &:deep(a) {
        text-decoration: none;
        color: var(--av-brand-primary);

        &:hover {
            color: var(--av-brand-secondary);
        }
    }
}

.a-button {
    width: 100%;
    margin: 24px 0 0;
    justify-content: center;
}
</style>
